import React from "react";
import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { StatusBadge } from "@xplatform/design-system";

/**
 * The cell kit of AdminTable. Every cell is ONE line (an optional second, dimmed
 * line for TwoLineCell), truncated, with the full value in a tooltip, so a row
 * keeps one height whatever the content. Typography and color are the design
 * system's text tokens: `sm` body, `xs` dimmed secondary, monospace for ids.
 */

const tooltipOf = (value: React.ReactNode): string | null =>
  typeof value === "string" || typeof value === "number" ? String(value) : null;

const Truncated: React.FC<{ value: React.ReactNode; tooltip?: string; children: React.ReactNode }> = ({
  value, tooltip, children,
}) => {
  const label = tooltip ?? tooltipOf(value);
  return label ? (
    <Tooltip label={label} openDelay={400} multiline maw={480}>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );
};

export const EMPTY = "-";

export const TextCell: React.FC<{
  value: React.ReactNode;
  /** Tooltip text; defaults to the value itself when it is a string or number. */
  tooltip?: string;
  mono?: boolean;
  dimmed?: boolean;
  strong?: boolean;
}> = ({ value, tooltip, mono, dimmed, strong }) => (
  <Truncated value={value} tooltip={tooltip}>
    <Text size="sm" truncate ff={mono ? "monospace" : undefined} c={dimmed ? "dimmed" : undefined}
          fw={strong ? 500 : undefined}>
      {value === null || value === undefined || value === "" ? EMPTY : value}
    </Text>
  </Truncated>
);

export const TwoLineCell: React.FC<{
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  mono?: boolean;
  align?: "left" | "right";
}> = ({ primary, secondary, mono, align = "left" }) => (
  <Stack gap={0} miw={0} align={align === "right" ? "flex-end" : "stretch"}>
    <Truncated value={primary}>
      <Text size="sm" fw={500} truncate>{primary ?? EMPTY}</Text>
    </Truncated>
    {secondary !== undefined && secondary !== null && secondary !== "" && (
      <Truncated value={secondary}>
        <Text size="xs" c="dimmed" truncate ff={mono ? "monospace" : undefined}>{secondary}</Text>
      </Truncated>
    )}
  </Stack>
);

export const StatusCell: React.FC<{ status: string | null | undefined }> = ({ status }) =>
  status ? <StatusBadge status={status} size="sm" /> : <Text size="sm" c="dimmed">{EMPTY}</Text>;

export interface RowAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: string;
  hidden?: boolean;
  loading?: boolean;
}

/** Icon buttons; clicks never bubble to the row's own click handler. */
export const ActionsCell: React.FC<{ actions: RowAction[] }> = ({ actions }) => (
  <Group gap="xs" justify="flex-end" wrap="nowrap">
    {actions.filter((a) => !a.hidden).map((action) => (
      <Tooltip key={action.label} label={action.label}>
        <ActionIcon
          variant="subtle"
          color={action.color}
          aria-label={action.label}
          loading={action.loading}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
        >
          {action.icon}
        </ActionIcon>
      </Tooltip>
    ))}
  </Group>
);
